import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { LinkInBioEditor } from "@/components/linkinbio/LinkInBioEditor";
import { FlowLinkInBio } from "@/components/booking-flow/FlowLinkInBio";
import { getBookingTheme, flowThemeVars, FLOW_DEFAULTS } from "@/components/booking-flow/themes";
import { LinkInBioAPI } from "@/lib/linkinbio-api";
import { LinkInBioProfile } from "@/types/linkinbio";
import { Loader2, ExternalLink } from "lucide-react";

const LinkInBioDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<LinkInBioProfile | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [bookingUrl, setBookingUrl] = useState<string | null>(null);
  const [flowThemeKey, setFlowThemeKey] = useState<string>("figgy_purple");
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/fancy-signin");
        return;
      }

      // Get business profile
      const { data: businessData } = await supabase
        .from("business_profiles")
        .select("id, booking_url, link_in_bio_enabled, booking_flow_theme_key")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!businessData) {
        navigate("/onboarding");
        return;
      }

      setBusinessId(businessData.id);
      setBookingUrl(businessData.booking_url);
      setFlowThemeKey(businessData.booking_flow_theme_key || "figgy_purple");
      setIsEnabled(businessData.link_in_bio_enabled || false);

      // Load link-in-bio profile
      const businessInfo = await LinkInBioAPI.getBusinessInfo(user.id);
      if (!businessInfo) {
        navigate("/onboarding");
        return;
      }

      const existingProfile = await LinkInBioAPI.getProfile(user.id);

      if (existingProfile) {
        setProfile(existingProfile);
      } else {
        // Create default profile
        const newProfile = await LinkInBioAPI.createProfile(user.id, {
          displayName: businessInfo.name,
          bio: 'Welcome to my business!',
          socialLinks: [
            { platform: 'instagram', url: businessInfo.instagram || '' },
            { platform: 'website', url: businessInfo.website || '' },
          ],
          customLinks: [],
          theme: 'minimal',
          font: 'default',
          buttonStyle: 'rounded',
          layout: 'business',
        });

        if (newProfile) {
          setProfile(newProfile);
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error("Failed to load your LinkInBio profile");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    if (!businessId) return;

    try {
      const { error } = await supabase
        .from("business_profiles")
        .update({ link_in_bio_enabled: enabled })
        .eq("id", businessId);

      if (error) throw error;

      setIsEnabled(enabled);
      toast.success(enabled ? "Link-in-bio is now live!" : "Link-in-bio turned off");
    } catch (error) {
      console.error("Error toggling link-in-bio:", error);
      toast.error("Failed to update status");
    }
  };

  const handleSave = async () => {
    if (!profile) return;

    try {
      setIsSaving(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/fancy-signin");
        return;
      }

      const updatedProfile = await LinkInBioAPI.updateProfile(user.id, profile);

      if (updatedProfile) {
        setProfile(updatedProfile);
        toast.success("Your LinkInBio page has been saved!");
      } else {
        throw new Error("Failed to save profile");
      }
    } catch (error: any) {
      console.error("Error saving profile:", error);
      toast.error(error.message || "Failed to save your LinkInBio page");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    if (bookingUrl) {
      window.open(`/${bookingUrl}`, '_blank');
    } else {
      toast.error("Please set up your booking URL in business settings first");
    }
  };

  if (isLoading || !profile) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const publicUrl = bookingUrl ? `${window.location.origin}/${bookingUrl}` : null;
  const flowTheme = getBookingTheme(flowThemeKey);

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold mb-6">Your LinkInBio Page</h1>

        <div className="grid gap-6 max-w-4xl mb-6">
          {/* Status Toggle */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Status</CardTitle>
                  <CardDescription>Enable or disable your link-in-bio page</CardDescription>
                </div>
                <Badge variant={isEnabled ? "default" : "secondary"}>
                  {isEnabled ? "Live" : "Off"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Link-in-Bio Page</p>
                  <p className="text-sm text-gray-600">
                    {isEnabled
                      ? "Your page is publicly visible"
                      : "Your page is not accessible to the public"}
                  </p>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={handleToggleEnabled}
                />
              </div>
            </CardContent>
          </Card>

          {/* Public URL */}
          {publicUrl && (
            <Card>
              <CardHeader>
                <CardTitle>Public URL</CardTitle>
                <CardDescription>Share this link to your link-in-bio page</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Input
                    value={publicUrl}
                    readOnly
                    className="flex-1 bg-gray-50"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handlePreview}
                    disabled={!isEnabled}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                {!isEnabled && (
                  <p className="text-sm text-yellow-600 mt-2">
                    Enable your link-in-bio page above to make this URL accessible
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Edit Your Page</CardTitle>
              </CardHeader>
              <CardContent>
                <LinkInBioEditor
                  profile={profile}
                  onProfileUpdate={(updates) => setProfile(prev => prev ? ({ ...prev, ...updates }) : null)}
                  onAddLink={() => {
                    setProfile(prev => {
                      if (!prev) return null;
                      const newLink = {
                        id: Date.now().toString(),
                        title: "New Link",
                        url: "https://",
                        enabled: true,
                        order: prev.customLinks.length,
                      };
                      return { ...prev, customLinks: [...prev.customLinks, newLink] };
                    });
                  }}
                  onUpdateLink={(updatedLink) => {
                    setProfile(prev => {
                      if (!prev) return null;
                      return {
                        ...prev,
                        customLinks: prev.customLinks.map(link =>
                          link.id === updatedLink.id ? updatedLink : link
                        ),
                      };
                    });
                  }}
                  onRemoveLink={(linkId) => {
                    setProfile(prev => {
                      if (!prev) return null;
                      return {
                        ...prev,
                        customLinks: prev.customLinks.filter(link => link.id !== linkId),
                      };
                    });
                  }}
                  onReorderLinks={(reorderedLinks) => {
                    setProfile(prev => {
                      if (!prev) return null;
                      return { ...prev, customLinks: reorderedLinks };
                    });
                  }}
                />

                <div className="flex gap-4 mt-8">
                  <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button variant="outline" onClick={handlePreview}>
                    Preview Page
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle>Preview</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* Renders the exact public link-in-bio (design system +
                    the provider's booking theme) so preview == live page */}
                <div
                  className="border rounded-lg overflow-hidden h-[600px]"
                  style={{
                    position: "relative",
                    background: flowTheme.flowBg || FLOW_DEFAULTS.bg,
                    ...flowThemeVars(flowTheme),
                  } as React.CSSProperties}
                >
                  <FlowLinkInBio
                    theme={flowTheme}
                    businessName={profile.displayName}
                    displayName={profile.displayName}
                    bio={profile.bio}
                    logoUrl={profile.profileImage}
                    socialLinks={profile.socialLinks.map((s) => ({ platform: s.platform, url: s.url }))}
                    customLinks={profile.customLinks
                      .filter((l) => l.enabled !== false)
                      .sort((a, b) => a.order - b.order)
                      .map((l) => ({ id: l.id, title: l.title, url: l.url }))}
                    onBook={() => {}}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkInBioDashboard;
